/**
 * CadenceRuleList
 * Node: N#1069  BRDs: 1355–1364
 *
 * Lists all cadence rules for the tenant.
 * - Obligation Name / row click opens the Create/Edit modal.
 * - Delete lives inside the edit modal's form footer.
 * - Status pills, search, sticky table header.
 *
 * Server-paginated: search + status filter are applied server-side (after the
 * backend's source/status enrichment, since obligation_name and the displayed
 * status are derived, not stored), and rows are returned one page at a time via
 * getRulesPaged. The page footer uses the shared <Pagination> control.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, Search, RefreshCw, Play, Rocket, Lock } from 'lucide-react';
import extractApiError from '../../utils/apiError';
import {
  CadenceRule,
  getRulesPaged,
  deleteRule,
  checkSourceStatus,
  rolloutNow,
} from '../../services/cadenceService';
import CadenceRuleForm from './CadenceRuleForm';
import DryRunModal from './DryRunModal';
import Modal from '../common/Modal';
import Badge from '../common/Badge';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmDelete, swalConfirm } from '../common/confirm';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { cadenceActivityForTypeCodes } from './cadenceModuleTypes';
import { statusVariant } from '../../utils/statusVariant';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';

const WF_KIND = 'sch_cadence_rule';

type StatusFilter = 'ALL' | 'Draft' | 'Active' | 'Paused' | 'Retired';

interface CadenceRuleListProps {
  compact?: boolean;
  /** In-context module filter: restrict the list + the create-form type picker
   *  to these registry type_codes. Omitted = standalone module (all cadences). */
  allowedTypeCodes?: string[];
}

const CadenceRuleList: React.FC<CadenceRuleListProps> = ({ compact = false, allowedTypeCodes }) => {
  const { formatDate } = useEntitySettings();
  const [rules, setRules]         = useState<CadenceRule[]>([]);
  const [workflowRow, setWorkflowRow] = useState<CadenceRule | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(20);
  const [loading, setLoading]     = useState(false);
  const [editRule, setEditRule]   = useState<CadenceRule | null>(null);
  const [showForm, setShowForm]   = useState(false);
  const [search, setSearch]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [reloadNonce, setReloadNonce] = useState(0);
  const [dryRunRule, setDryRunRule] = useState<CadenceRule | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [rollingId, setRollingId] = useState<number | null>(null);
  const [formFooter, setFormFooter] = useState<React.ReactNode>(null);

  // RBAC (extent-only, GRC persona): a user may mutate cadence rules in this view
  // if they hold view+modify on the matching per-module cadence activity (in-context
  // tab) or on SCH_GLOBAL_CADENCE (standalone Cadence Engine page). Read stays open.
  const { check } = usePermissions();
  const cadenceActivity = useMemo(() => cadenceActivityForTypeCodes(allowedTypeCodes), [allowedTypeCodes]);
  const { allowed: canView, loading: viewLoading } = usePermission(cadenceActivity, 'view');
  const canModify = check(cadenceActivity, 'view+modify').allowed;

  // Stable string key for the (array) in-context filter so effects don't refetch
  // on every parent render that hands us a fresh array instance.
  const codesKey = allowedTypeCodes && allowedTypeCodes.length ? allowedTypeCodes.join(',') : '';

  // Debounce the free-text search before it hits the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter/search/page-size change resets to the first page.
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, pageSize, codesKey]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    getRulesPaged({
      page,
      limit: pageSize,
      search: debouncedSearch || undefined,
      rule_status: statusFilter === 'ALL' ? undefined : statusFilter,
      source_type_codes: codesKey || undefined,
    })
      .then(res => {
        if (cancelled) return;
        setRules(res.items);
        setTotalRecords(res.total);
      })
      .catch(e => {
        if (cancelled) return;
        setRules([]);
        setTotalRecords(0);
        setToast({ type: 'error', message: extractApiError(e, 'Failed to load cadence rules.') });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, debouncedSearch, statusFilter, codesKey, reloadNonce, canView]);

  /** Refetch the current page (used after create / edit / delete / source-check). */
  const load = useCallback(() => setReloadNonce(n => n + 1), []);

  const filtersActive = debouncedSearch !== '' || statusFilter !== 'ALL';

  const handleNew    = () => { setEditRule(null); setFormFooter(null); setShowForm(true); };
  const handleEdit   = (r: CadenceRule) => { setEditRule(r); setFormFooter(null); setShowForm(true); };
  const handleSaved  = () => { setShowForm(false); setEditRule(null); setFormFooter(null); load(); };
  const handleCancel = () => { setShowForm(false); setEditRule(null); setFormFooter(null); };

  const handleDeleteFromForm = async (r: CadenceRule) => {
    const ok = await confirmDelete({
      title: `Delete "${r.obligation_name || `Rule #${r.id}`}"?`,
      text: 'This action cannot be undone.',
    });
    if (!ok) return;
    try {
      await deleteRule(r.id);
      setShowForm(false);
      setEditRule(null);
      setFormFooter(null);
      load();
      setToast({ type: 'delete', message: 'Cadence rule deleted.' });
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Delete failed.') });
    }
  };

  // Manual rollout vent — fire this rule's due occurrences NOW via the engine
  // dispatcher (same path the scheduler will use). Idempotent server-side.
  const handleRolloutNow = async (r: CadenceRule) => {
    const ok = await swalConfirm({
      title: `Roll out "${r.obligation_name || `rule #${r.id}`}" now?`,
      text: 'This fires its due occurrences into the downstream execution tables (re-running is safe — already-fired occurrences are skipped).',
    });
    if (!ok) return;
    setRollingId(r.id);
    try {
      const res = await rolloutNow(r.id);
      setToast({
        type: 'success',
        message: `Rolled out: ${res.formup.calendar_committed} obligation(s) created (run #${res.formup.run_id}).`,
      });
      load();
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Rollout failed.') });
    } finally {
      setRollingId(null);
    }
  };

  const handleCheckSources = async () => {
    try {
      const result = await checkSourceStatus();
      setToast({
        type: result.paused_rule_ids.length > 0 ? 'warning' : 'success',
        message: result.message,
      });
      if (result.paused_rule_ids.length > 0) load();
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Source-status check failed.') });
    }
  };

  // ── List view ───────────────────────────────────────────────────────
  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to cadence rules." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">

      {/* Toolbar — Show LEFT · filters + search + Create RIGHT */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={statusFilter}
            onChange={v => setStatusFilter((v ?? 'ALL') as StatusFilter)}
            className="filter-status"
            data-testid="SEL_cadence_rule_status_filter"
            noSearchIcon
            options={[
              { value: 'ALL', label: 'All status' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Active', label: 'Active' },
              { value: 'Paused', label: 'Paused' },
              { value: 'Retired', label: 'Retired' },
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search by obligation, category, template…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_cadence_rule_search"
          />
          <Button variant="secondary-outline" icon={RefreshCw} onClick={handleCheckSources} data-testid="BTN_cadence_rule_check_sources">
            Check Source Status
          </Button>
          {canModify && (
            <Button variant="fill" icon={Plus} onClick={handleNew} data-testid="BTN_cadence_rule_create">
              Create
            </Button>
          )}
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Category</th>
                    <th className="table-list-th">Obligation</th>
                    <th className="table-list-th">Handler</th>
                    <th className="table-list-th">Template</th>
                    <th className="table-list-th">Start</th>
                    <th className="table-list-th">End</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th" style={{ textAlign: 'center' }}>Dry Run</th>
                    <th className="table-list-th" style={{ textAlign: 'center' }}>Rollout</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && (
                    <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'md', 'xs', '2xs', '2xs', 'pill', 'sm', 'sm']} />
                  )}
                  {!loading && rules.length === 0 && (
                    <tr>
                      <td colSpan={11} className="table-list-cell">
                        {filtersActive ? (
                          <NoDataFound
                            title="No matches"
                            message="No rules match the current search."
                            action={(
                              <Button
                                variant="secondary-outline"
                                onClick={() => { setSearch(''); setStatusFilter('ALL'); }}
                                data-testid="BTN_cadence_rule_clear_filters"
                              >
                                Clear filters
                              </Button>
                            )}
                          />
                        ) : (
                          <NoDataFound
                            title="No cadence rules yet"
                            message="Create your first rule to define when obligation events fire."
                          />
                        )}
                      </td>
                    </tr>
                  )}
                  {!loading && rules.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="table-list-row"
                      onClick={() => handleEdit(r)}
                      data-testid={`BTN_cadence_rule_row_${r.id}_open`}
                    >
                      <td className="table-list-cell" onClick={e => { e.stopPropagation(); setWorkflowRow(r); }} title="Workflow — view timeline & act">
                        <ApprovalDot
                          state={(((r as any).workflow_state) || 'draft') as WorkflowState}
                          onClick={() => setWorkflowRow(r)}
                          title="Workflow — view timeline & act"
                        />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          {r.category_name || r.category_id}
                        </span>
                      </td>
                      <td className="table-list-cell" onClick={e => e.stopPropagation()}>
                        <span className="cell-name-lock">
                          {(r as any).workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          <a
                            href="#"
                            className="table-list-cell-title"
                            onClick={e => { e.preventDefault(); handleEdit(r); }}
                            title={r.obligation_description || ''}
                          >
                            {r.obligation_name || `Rule #${r.id}`}
                          </a>
                        </span>
                        {r.obligation_description && (
                          <div className="table-list-cell-sub">{r.obligation_description}</div>
                        )}
                      </td>
                      <td className="table-list-cell">{r.fulfillment_handler || '—'}</td>
                      <td className="table-list-cell">
                        {r.template_reference || '—'}
                      </td>
                      <td className="table-list-cell">{r.start_date ? formatDate(r.start_date) : '—'}</td>
                      <td className="table-list-cell">
                        {r.end_date ? formatDate(r.end_date) : <span className="italic text-slate-400 text-xs">Open-ended</span>}

                      </td>
                      <td className="table-list-cell table-list-cell--status">
                        <Badge variant={statusVariant(r.rule_status)} size="sm">{r.rule_status}</Badge>
                      </td>
                      <td className="table-list-cell" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <Button
                          variant="secondary-outline"
                          icon={Play}
                          title={`Dry-run ${r.obligation_name || `rule #${r.id}`} — projection only, no register writes`}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDryRunRule(r); }}
                          data-testid={`BTN_cadence_rule_row_${r.id}_dry_run`}
                        >
                          Dry Run
                        </Button>
                      </td>
                      <td className="table-list-cell" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <Button
                          variant="secondary-outline"
                          icon={Rocket}
                          disabled={rollingId === r.id}
                          title={`Roll out ${r.obligation_name || `rule #${r.id}`} now — fires due occurrences into the downstream tables`}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleRolloutNow(r); }}
                          data-testid={`BTN_cadence_rule_row_${r.id}_rollout_now`}
                        >
                          {rollingId === r.id ? 'Firing…' : 'Rollout'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="cadence rules"
            />
          </div>
        </div>
      </div>

      {/* Per-rule dry-run modal — opens on Dry Run button click */}
      {dryRunRule && (
        <DryRunModal rule={dryRunRule} onClose={() => setDryRunRule(null)} />
      )}

      {/* Create / Edit rule modal */}
      <Modal isOpen={showForm} onClose={handleCancel} size="lg" closeOnOverlayClick={false}>
        <Modal.Header>
          {editRule
            ? <>Edit "<span className="item-highlight">{editRule.obligation_name || `Rule #${editRule.id}`}</span>"</>
            : 'Create Cadence Rule'}
        </Modal.Header>
        <Modal.Body className='p-0'>
          <CadenceRuleForm
            rule={editRule ?? undefined}
            onSaved={handleSaved}
            onCancel={handleCancel}
            onDelete={editRule ? () => handleDeleteFromForm(editRule) : undefined}
            renderFooter={setFormFooter}
            allowedTypeCodes={allowedTypeCodes}
            canModify={canModify}
          />
        </Modal.Body>
        <Modal.Footer className="app-modal-footer--split">
          {formFooter}
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => { setWorkflowRow(null); load(); }}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={load}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default CadenceRuleList;
